import { useState, FormEvent } from 'react';
import { httpsCallable } from 'firebase/functions';
import { sendSignInLinkToEmail } from 'firebase/auth';
import { auth, functions } from '../config/firebase';
import { useNavigate } from 'react-router-dom';

export default function HomeLeadMagnet() {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    const normalizedEmail = email.toLowerCase().trim();

    try {
      // 1. Create lead and entitlement
      const createLead = httpsCallable(functions, 'createLead');
      await createLead({
        email: normalizedEmail,
        name: name.trim() || 'Friend',
        source: 'lead-magnet',
      });

      // 2. Send magic link for passwordless authentication
      // The magic link handler will process the sign-in and redirect to portal
      const actionCodeSettings = {
        url: `${window.location.origin}/magic-link?redirect=/portal?welcome=true`,
        handleCodeInApp: true,
      };

      await sendSignInLinkToEmail(auth, normalizedEmail, actionCodeSettings);

      // 3. Store email in localStorage for magic link callback
      window.localStorage.setItem('emailForSignIn', normalizedEmail);

      setSuccess(true);
    } catch (err: any) {
      console.error('Error creating lead:', err);
      setError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      {/* Hero Section */}
      <section className="mx-auto max-w-4xl px-4 py-16 text-center">
        <h1 className="text-5xl font-bold text-primary mb-4">
          Close Deals Faster. Reduce Legal Risk.
        </h1>
        <p className="text-xl text-secondary mb-8">
          Download the <strong>SA SME Essential Contract Clauses Checklist</strong> —
          the must-have legal checklist for South African businesses.
        </p>

        {!success ? (
          <form onSubmit={handleSubmit} className="max-w-md mx-auto">
            <div className="mb-4">
              <input
                type="text"
                required
                placeholder="Your Name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div className="mb-4">
              <input
                type="email"
                required
                placeholder="you@company.co.za"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-4 py-3 text-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            <div className="mb-4 flex items-start text-left">
              <input
                type="checkbox"
                id="consent"
                required
                checked={consent}
                onChange={(e) => setConsent(e.target.checked)}
                className="mt-1 mr-3"
              />
              <label htmlFor="consent" className="text-sm text-gray-600">
                I consent to receiving the checklist and future helpful resources about legal templates.
                You can unsubscribe anytime.
              </label>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md text-red-600 text-sm">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-600 text-white px-6 py-3 rounded-md font-medium text-lg hover:bg-blue-700 transition-colors disabled:bg-gray-400"
            >
              {loading ? 'Creating Your Account...' : 'Create Free Account & Get Checklist'}
            </button>

            <p className="mt-3 text-xs text-gray-600 text-center">
              No credit card required. Instant access.
            </p>
          </form>
        ) : (
          <div className="max-w-md mx-auto p-6 bg-green-50 border border-green-200 rounded-lg">
            <h3 className="text-xl font-semibold text-green-800 mb-2">Success! Check Your Email 🎉</h3>
            <p className="text-green-700 mb-4">
              We've sent you a secure link to access your portal and download your free checklist.
              <strong> Click the link in your email to sign in.</strong>
            </p>
            <p className="text-sm text-green-600 mb-4">
              💡 <strong>Tip:</strong> Check your spam folder if you don't see it in a few minutes.
            </p>
            <button
              onClick={() => navigate('/portal/products')}
              className="text-blue-600 hover:underline font-medium"
            >
              View our template packs →
            </button>
          </div>
        )}
      </section>

      {/* Value Proposition */}
      <section className="mx-auto max-w-5xl px-4 py-16">
        <h2 className="text-3xl font-bold text-primary text-center mb-12">
          What's Inside the Checklist
        </h2>
        <div className="grid md:grid-cols-3 gap-8">
          <div className="text-center">
            <div className="text-4xl mb-4">✅</div>
            <h3 className="text-xl font-semibold mb-2">12 Essential Clauses</h3>
            <p className="text-secondary">
              Every contract clause South African SMEs need to protect cashflow and avoid disputes.
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">🇿🇦</div>
            <h3 className="text-xl font-semibold mb-2">SA-Specific</h3>
            <p className="text-secondary">
              POPIA-aware, local business norms, and South African legal context.
            </p>
          </div>
          <div className="text-center">
            <div className="text-4xl mb-4">⚡</div>
            <h3 className="text-xl font-semibold mb-2">Instant Download</h3>
            <p className="text-secondary">
              PDF delivered instantly to your inbox. No waiting, no hassle.
            </p>
          </div>
        </div>
      </section>

      {/* Social Proof / Authority */}
      <section className="bg-primary text-white py-16">
        <div className="mx-auto max-w-3xl px-4 text-center">
          <h3 className="text-2xl font-semibold mb-4">About the Author</h3>
          <p className="text-lg mb-2">
            <strong>Justin Benjamin</strong> — Commercial Attorney & Founder, Benjamin & Associates Inc.
          </p>
          <p className="text-gray-300">
            12+ years in commercial law and deal structuring.
            Helping South African SMEs close deals faster, with less legal risk.
          </p>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mx-auto max-w-3xl px-4 py-16 text-center">
        <h2 className="text-3xl font-bold text-primary mb-4">
          Ready to Protect Your Business?
        </h2>
        <p className="text-lg text-secondary mb-8">
          Get the checklist and take the first step toward safer, faster deals.
        </p>
        <a
          href="#"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="inline-block bg-blue-600 text-white px-8 py-3 rounded-md font-medium text-lg hover:bg-blue-700 transition-colors"
        >
          Get the Free Checklist
        </a>
      </section>
    </div>
  );
}
